// InternalReportRow — Campaña agent-noise-collapse (resultados 1 y 2).
//
// UNA fila para lo que antes era un muro: icono, título ("Informe interno
// de <ayudante>" / "Aviso del sistema"), resumen de una línea y un
// expansor `▽` que revela el texto crudo.
//
// Founder-first §3 (dos capas) al pie de la letra: la capa founder es
// SIEMPRE el título + el resumen; el cuerpo crudo vive detrás del
// expansor, cerrado por defecto. Nada se borra — se pliega.
//
// Reutiliza `CollapseHeaderRow`, el mismo cromo que ya usan
// `AggregationCard` y `WorkSegment`, para que un informe interno se lea
// como el resto del ruido plegado de la conversación y no como un widget
// nuevo. El cuerpo se pinta `whitespace-pre-wrap` en vez de pasarlo por
// `MarkdownRenderer` a propósito: el texto crudo es la capa developer, y
// renderizar markdown ahí lo volvería a convertir en un muro con
// encabezados, que es exactamente lo que esta fila existe para evitar.

import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { CollapseHeaderRow } from './CollapseHeaderRow';
import { firstSentence, type NotificationClassification } from '../../lib/tool-rendering/agent-noise';

export interface InternalReportRowProps {
  /** `null` = huérfano sin forma de notificación: título genérico. */
  classification: NotificationClassification | null;
  /** El texto crudo, íntegro. Sólo visible tras pulsar el expansor. */
  raw: string;
  messageId: string;
}

export const INTERNAL_REPORT_TEST_ID = 'internal-report-row';

export function InternalReportRow({ classification, raw, messageId }: InternalReportRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Tres títulos, en orden de cuánta verdad tenemos: nombre del ayudante
  // > "un ayudante" > "aviso del sistema".
  const title =
    classification?.kind === 'system'
      ? t('internalReport.system')
      : classification?.helperName
        ? t('internalReport.helperNamed', { name: classification.helperName })
        : t('internalReport.helperAnonymous');

  // Un huérfano (`classification === null`) no trae `<summary>`: no es una
  // notificación, es el texto del ayudante tal cual. Su resumen es la
  // primera frase del propio cuerpo — la MISMA función que usa el
  // clasificador, para que las dos clases de fila se lean igual.
  const summary = classification?.summary ?? firstSentence(raw);

  return (
    <CollapseHeaderRow
      icon={classification?.kind === 'system' ? 'Cog' : 'Bot'}
      iconClassName="text-muted-foreground/70"
      headline={
        <>
          <span className="text-muted-foreground">{title}</span>
          {summary !== '' && (
            <>
              <span className="text-muted-foreground/45"> · </span>
              <span className="text-muted-foreground/70" data-testid={`${INTERNAL_REPORT_TEST_ID}-summary`}>
                {summary}
              </span>
            </>
          )}
        </>
      }
      headlineClassName="text-muted-foreground"
      open={open}
      onToggle={() => setOpen((v) => !v)}
      rootClassName="py-1"
      rootTestId={INTERNAL_REPORT_TEST_ID}
      headerTestId={`${INTERNAL_REPORT_TEST_ID}-header`}
      rootProps={{
        'data-message-id': messageId,
        'data-report-kind': classification?.kind ?? 'orphan',
      }}
      ariaLabel={title}
    >
      {open && (
        <div
          className="max-h-96 overflow-y-auto rounded-md bg-muted/30 px-3 py-2"
          data-testid={`${INTERNAL_REPORT_TEST_ID}-raw`}
        >
          <div className="text-caption leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
            {raw}
          </div>
        </div>
      )}
    </CollapseHeaderRow>
  );
}
